import { ADMIN_SECTIONS } from '@/config.admin';
import { isAdminCategoryListKey } from '@/composables/useAdminCategoryList';
import { ERouteName } from '@/typescript/enums/ERouteName';
import ProductSelectionView from '@/views/ProductSelectionView.vue';
import LoginView from '@/views/LoginView.vue';
import { useAuth } from '@/composables/useAuth';
import { useAuthStore } from '@/store/auth.store';
import { isKnownClientSlug } from '@/clients/clientRegistry';
import { createRouter, createWebHistory } from 'vue-router';

const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: '/login',
            name: ERouteName.Login,
            component: LoginView,
        },
        // Back-office mock-up (ticket #8): simulated data only, no API call, hence no guard
        // yet. The guard moves back onto these routes when the /admin API is wired.
        {
            path: '/admin',
            component: () => import('@/views/AdminShellView.vue'),
            children: [
                { path: '', name: ERouteName.Admin, redirect: '/admin/references' },
                {
                    path: 'references',
                    name: ERouteName.AdminReferences,
                    component: () => import('@/views/AdminReferenceListView.vue'),
                },
                {
                    path: 'references/:code',
                    name: ERouteName.AdminReferenceDetail,
                    component: () => import('@/views/AdminReferenceDetailView.vue'),
                },
                {
                    path: 'modeles',
                    name: ERouteName.AdminTemplates,
                    component: () => import('@/views/AdminTemplateListView.vue'),
                },
                {
                    path: 'modeles/thematiques',
                    name: ERouteName.AdminTemplateThemes,
                    component: () => import('@/views/AdminCategoriesView.vue'),
                    props: { list: 'modeles' },
                },
                {
                    path: 'modeles/:id',
                    name: ERouteName.AdminTemplateDetail,
                    component: () => import('@/views/AdminTemplateDetailView.vue'),
                },
                {
                    path: 'bibliotheque',
                    name: ERouteName.AdminLibrary,
                    component: () => import('@/views/AdminLibraryView.vue'),
                },
                {
                    path: 'bibliotheque/categories/:list',
                    name: ERouteName.AdminLibraryCategories,
                    component: () => import('@/views/AdminCategoriesView.vue'),
                    props: true,
                    beforeEnter: (to) => (isAdminCategoryListKey(String(to.params.list)) ? undefined : '/admin/bibliotheque'),
                },
                {
                    path: 'projets',
                    name: ERouteName.AdminProjects,
                    component: () => import('@/views/AdminProjectListView.vue'),
                },
                {
                    path: 'projets/:id',
                    name: ERouteName.AdminProjectDetail,
                    component: () => import('@/views/AdminProjectDetailView.vue'),
                },
                {
                    path: 'reglages-ia',
                    name: ERouteName.AdminAiSettings,
                    component: () => import('@/views/AdminAiSettingsView.vue'),
                },
                ...ADMIN_SECTIONS.filter((section) => section.isUpcoming).map((section) => ({
                    path: section.path.replace('/admin/', ''),
                    component: () => import('@/views/AdminUpcomingView.vue'),
                })),
            ],
        },
        // The editor in design mode, full screen, outside the back-office shell (mock-up, no guard).
        {
            path: '/admin/modeles/:id/editeur',
            name: ERouteName.AdminTemplateEditor,
            component: () => import('@/views/AdminTemplateEditorView.vue'),
        },
        // The same editor on a customer's project, to correct it (ADM-04).
        {
            path: '/admin/projets/:id/editeur',
            name: ERouteName.AdminProjectEditor,
            component: () => import('@/views/AdminProjectEditorView.vue'),
        },
        // The socle's product admin, kept for comparison while the mock-up replaces it.
        {
            path: '/admin/socle',
            name: ERouteName.AdminLegacy,
            component: () => import('@/views/AdminView.vue'),
        },
        {
            path: '/admin/configurator/:productName',
            name: ERouteName.ConfiguratorAdmin,
            component: () => import('@/views/EditorView.vue'),
        },
        {
            path: '/admin/configurator/:productName/:templateName',
            name: ERouteName.ConfiguratorAdminTemplate,
            component: () => import('@/views/EditorView.vue'),
        },
        {
            path: '/:clientSlug?',
            children: [
                {
                    path: '',
                    name: ERouteName.ProductSelection,
                    component: ProductSelectionView,
                },
                {
                    path: 'configurator/:productName',
                    name: ERouteName.Configurator,
                    component: () => import('@/views/EditorView.vue'),
                },
                {
                    path: 'configurator/:productName/:templateName',
                    name: ERouteName.ConfiguratorTemplate,
                    component: () => import('@/views/EditorView.vue'),
                },
            ],
        },
        {
            path: '/:pathMatch(.*)*',
            redirect: '/',
        },
    ],
});

const ADMIN_ROUTES = [ERouteName.AdminLegacy, ERouteName.ConfiguratorAdmin, ERouteName.ConfiguratorAdminTemplate];

const CLIENT_ROUTES = [ERouteName.ProductSelection, ERouteName.Configurator, ERouteName.ConfiguratorTemplate];

function getClientSlugParam(slug: string | string[] | undefined): string {
    return typeof slug === 'string' ? slug : '';
}

router.beforeEach(async (to, from) => {
    const authStore = useAuthStore();
    const { checkAuth } = useAuth();

    await checkAuth();

    if (ADMIN_ROUTES.includes(to.name as ERouteName) && !authStore.isAuthenticated) {
        return { name: ERouteName.Login };
    }

    if (!CLIENT_ROUTES.includes(to.name as ERouteName)) {
        return undefined;
    }

    const targetSlug = getClientSlugParam(to.params.clientSlug);

    if (targetSlug.length > 0 && !isKnownClientSlug(targetSlug)) {
        return { ...to, params: { ...to.params, clientSlug: '' } };
    }

    const previousSlug = getClientSlugParam(from.params.clientSlug);

    if (targetSlug.length === 0 && previousSlug.length > 0) {
        return { ...to, params: { ...to.params, clientSlug: previousSlug } };
    }

    return undefined;
});

export { router };
